import { FAMILY_LABEL, type AgentSummary, type SignalFamily, type SignalHeatCell } from "@ti/core";
import { Link } from "@tanstack/react-router";
import { Bot } from "lucide-react";
import { useMemo } from "react";
import { Tooltip } from "@/components/ui/tooltip";
import { fmtInt, fmtPct } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Taxonomy order (A–J) — fixed so columns never reshuffle. */
export const FAMILIES = Object.keys(FAMILY_LABEL) as SignalFamily[];

const RAMP = ["var(--seq-100)", "var(--seq-250)", "var(--seq-400)", "var(--seq-550)", "var(--seq-700)"] as const;
const LEVELS = RAMP.length;

/**
 * Square-root scale: counts are heavy-tailed, so a linear ramp would leave most cells in the
 * lightest step. Level 0 is reserved for an exact zero.
 */
function level(count: number, max: number) {
  if (count <= 0 || max <= 0) return 0;
  return Math.min(LEVELS, Math.max(1, Math.ceil(Math.sqrt(count / max) * LEVELS)));
}

/** Upper bound (inclusive) of each level, for the legend. */
function levelBounds(max: number) {
  return Array.from({ length: LEVELS }, (_, i) => Math.round(max * ((i + 1) / LEVELS) ** 2));
}

export function SignalHeatmap({
  cells,
  agents,
  activeFamily,
  onSelectFamily,
}: {
  cells: SignalHeatCell[];
  agents: AgentSummary[] | undefined;
  activeFamily?: SignalFamily;
  onSelectFamily: (f: SignalFamily) => void;
}) {
  const { rows, max, colTotals, grand } = useMemo(() => {
    const byKey = new Map(cells.map((c) => [`${c.agentId}|${c.family}`, c.count]));
    const agentIds = agents?.length ? agents.map((a) => a.id) : [...new Set(cells.map((c) => c.agentId))];
    const names = new Map(agents?.map((a) => [a.id, a.name]));
    let max = 0;
    const colTotals = new Map<SignalFamily, number>();
    const rows = agentIds.map((id) => {
      const counts = FAMILIES.map((f) => {
        const n = byKey.get(`${id}|${f}`) ?? 0;
        max = Math.max(max, n);
        colTotals.set(f, (colTotals.get(f) ?? 0) + n);
        return n;
      });
      return { id, name: names.get(id) ?? id, counts, total: counts.reduce((s, n) => s + n, 0) };
    });
    const grand = rows.reduce((s, r) => s + r.total, 0);
    return { rows, max, colTotals, grand };
  }, [cells, agents]);

  const bounds = levelBounds(max);

  return (
    <div>
      <div className="scrollbar-thin overflow-x-auto">
        <table className="w-full min-w-[880px] table-fixed border-separate border-spacing-[3px] text-[12px]">
          <caption className="sr-only">Signal count by agent and signal family, last 7 days</caption>
          <colgroup>
            <col className="w-[160px]" />
            {FAMILIES.map((f) => (
              <col key={f} />
            ))}
            <col className="w-[64px]" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="px-2 pb-1 text-left align-bottom text-[11px] font-medium uppercase tracking-wide text-fg-3">
                Agent
              </th>
              {FAMILIES.map((f) => (
                <th key={f} scope="col" className="px-1 pb-1 align-bottom">
                  <button
                    type="button"
                    onClick={() => onSelectFamily(f)}
                    aria-pressed={activeFamily === f}
                    title={`Filter catalog to ${FAMILY_LABEL[f]}`}
                    className={cn(
                      "w-full cursor-pointer rounded px-0.5 text-center text-[11px] font-medium leading-tight hover:text-fg",
                      activeFamily === f ? "text-accent" : "text-fg-2",
                    )}
                  >
                    {FAMILY_LABEL[f]}
                  </button>
                </th>
              ))}
              <th scope="col" className="px-2 pb-1 text-right align-bottom text-[11px] font-medium uppercase tracking-wide text-fg-3">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <th scope="row" className="truncate px-2 text-left font-normal">
                  <Link
                    to="/traces"
                    search={{ agentId: r.id, severity: "low" }}
                    className="inline-flex max-w-full items-center gap-1 font-mono text-[12px] text-fg hover:text-accent hover:underline"
                    title={`Flagged traces for ${r.name}`}
                  >
                    <Bot className="size-3.5 shrink-0 text-fg-3" />
                    <span className="truncate">{r.name}</span>
                  </Link>
                </th>
                {r.counts.map((n, i) => {
                  const f = FAMILIES[i]!;
                  const lv = level(n, max);
                  const share = r.total ? n / r.total : 0;
                  return (
                    <td key={f} className="p-0">
                      <Tooltip
                        content={
                          <span className="block">
                            <span className="font-medium">{r.name}</span> · {FAMILY_LABEL[f]}
                            <span className="block text-fg-2 tabular">
                              {fmtInt(n)} signals · {fmtPct(share)} of agent's total
                            </span>
                            <span className="block text-fg-3">Click to open matching traces</span>
                          </span>
                        }
                      >
                        <Link
                          to="/traces"
                          search={{ q: `agent:${r.name} family:${f}` }}
                          aria-label={`${r.name}, ${FAMILY_LABEL[f]}: ${n} signals — open matching traces`}
                          className={cn(
                            "grid h-9 w-full place-items-center rounded-[4px] text-[12px] tabular transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-1",
                            lv === 0 && "bg-surface-2 text-fg-3",
                            lv >= 3 && "font-semibold",
                            activeFamily && activeFamily !== f && "opacity-45",
                          )}
                          style={lv ? { background: RAMP[lv - 1], color: lv <= 2 ? "var(--text)" : "var(--background)" } : undefined}
                        >
                          {n === 0 ? "·" : fmtInt(n)}
                        </Link>
                      </Tooltip>
                    </td>
                  );
                })}
                <td className="px-2 text-right font-medium text-fg tabular">{fmtInt(r.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" className="px-2 pt-1 text-left text-[11px] font-medium uppercase tracking-wide text-fg-3">
                Total
              </th>
              {FAMILIES.map((f) => (
                <td key={f} className="pt-1 text-center text-[12px] text-fg-2 tabular">
                  {fmtInt(colTotals.get(f) ?? 0)}
                </td>
              ))}
              <td className="px-2 pt-1 text-right font-semibold text-fg tabular">{fmtInt(grand)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-1 text-[11px] text-fg-3">
        <span>Cells open matching traces · column headers filter the catalog · agent names open all flagged traces.</span>
        <span className="flex items-center gap-1.5" aria-label="Color scale">
          <span>Fewer</span>
          <span className="flex items-center gap-0.5">
            <span className="h-3 w-6 rounded-sm bg-surface-2" title="0" />
            {RAMP.map((c, i) => (
              <span key={c} className="h-3 w-6 rounded-sm" style={{ background: c }} title={`${i === 0 ? 1 : bounds[i - 1]! + 1}–${bounds[i]}`} />
            ))}
          </span>
          <span>More</span>
          <span className="ml-1 tabular">(0 – {fmtInt(max)})</span>
        </span>
      </div>
    </div>
  );
}
